<ng-container *transloco="let t">
  @let paymentData = digitalPayment();

  @let method = paymentMethod();
  @let methodLabel = collectedMethodsLabel() || method?.name;

  @if (methodLabel || canViewPayments()) {
    <div class="order-section-item">
      <div class="order-section-item-title">
        {{ t('orderDetails.paymentMethod') }}
      </div>
      <div class="order-section-item-value">
        <span class="order-details-payment-method__method">
          {{ methodLabel }}
          @if (canViewPayments()) {
            <pxw-tooltip [text]="t('orderDetails.viewPayments')" [offsetY]="10">
              <button
                type="button"
                class="order-details-payment-method__payments-link"
                [attr.aria-label]="t('orderDetails.viewPayments')"
                (click)="viewPayments.emit()"
              >
                <pxw-icon icon="info" size="xs" />
              </button>
            </pxw-tooltip>
          }
        </span>
      </div>
    </div>
  }

  @if (!collectedMethodsLabel() && method?.type === 'CASH' && howMuch()) {
    <div class="order-section-item">
      <div class="order-section-item-title">
        {{ t('orderDetails.paymentHowMuch') }}
      </div>
      <div class="order-section-item-value">
        {{ howMuch()! | currencyFormat }}
      </div>
    </div>
  }
  @if (collectedByMarketplaceLabel(); as collectedByMarketplaceLabel) {
    <div class="order-section-item">
      <div class="order-section-item-title">{{ t('paymentStatus.title') }}</div>
      <div class="order-section-item-value">
        <pxw-tag type="success" size="xs">{{ collectedByMarketplaceLabel }}</pxw-tag>
      </div>
    </div>
  } @else if (paymentDisplayStatus(); as displayStatus) {
    <div class="order-section-item">
      <div class="order-section-item-title">{{ t('paymentStatus.title') }}</div>
      <div class="order-section-item-value">
        <pxw-payment-status-tag [status]="displayStatus" />
      </div>
    </div>
  }

  @if (unmatchedPayment(); as notice) {
    <div class="order-section-item">
      <div class="order-section-item-title">
        {{ t('orderDetails.unmatchedTransfer', { outcome: notice.outcome }) }}
      </div>
      <div class="order-section-item-value">
        {{
          t('orderDetails.unmatchedTransferDetail', {
            outcome: notice.outcome,
            reason: notice.reason,
            amount: (notice.amount | currencyFormat),
            payer:
              notice.payerName ??
              notice.payerTaxId ??
              t('orderDetails.unmatchedTransferUnknownPayer'),
          })
        }}
      </div>
    </div>
  }

  @if (canMarkPaid()) {
    <div class="order-section-item">
      <div class="order-section-item-title">{{ t('orderDetails.markAsPaid') }}</div>
      <div class="order-section-item-value">
        <pxw-button
          appareance="outline"
          color="blue"
          size="sm"
          [rounded]="true"
          (click)="markPaid.emit()"
        >
          {{ t('orderDetails.markAsPaid') }}
        </pxw-button>
      </div>
    </div>
  }

  @if (canRegisterRefund() && refundableDisplayAmount() > 0) {
    <div class="order-section-item">
      <div class="order-section-item-title">
        {{ t(surplusAmount() > 0 ? 'orderDetails.refundSurplus' : 'orderDetails.registerRefund') }}
      </div>
      <div class="order-section-item-value order-section-item-value--surplus">
        <strong>{{ refundableDisplayAmount() | currencyFormat }}</strong>
        <pxw-button
          appareance="outline"
          color="blue"
          size="sm"
          [rounded]="true"
          (click)="registerRefund.emit()"
        >
          {{ t('orderDetails.registerRefund') }}
        </pxw-button>
      </div>
    </div>
  }

  @if (paymentData) {
    <!-- Payment Link -->
    @if (paymentData.type === 'link' && paymentUrl()) {
      <div class="order-section-item">
        <div class="order-section-item-title">
          {{ t('orderDetails.paymentLink') }}
        </div>
        <div class="order-section-item-value">
          <a [href]="paymentUrl()" target="blank">{{ t('orderDetails.openPaymentLink') }}</a>
        </div>
      </div>
    }

    <!-- Bank Transfer Details -->
    @if (paymentData.type === 'transfer') {
      @if (paymentData.cvu) {
        <div class="order-section-item">
          <div class="order-section-item-title">CVU/CBU</div>
          <div class="order-section-item-value">{{ paymentData.cvu }}</div>
        </div>
      }
      @if (paymentData.alias) {
        <div class="order-section-item">
          <div class="order-section-item-title">Alias</div>
          <div class="order-section-item-value">{{ paymentData.alias }}</div>
        </div>
      }
      @if (paymentData.accountHolder) {
        <div class="order-section-item">
          <div class="order-section-item-title">
            {{ t('orderMessage.accountHolderTitle') }}
          </div>
          <div class="order-section-item-value">{{ paymentData.accountHolder }}</div>
        </div>
      }
    }

    @if (canRefund()) {
      <div class="order-section-item">
        <div class="order-section-item-title">{{ t('orderDetails.refundPayment') }}</div>
        <div class="order-section-item-value">
          <pxw-button
            appareance="outline"
            color="pink"
            size="sm"
            [rounded]="true"
            [loading]="refundingPayment()"
            [disabled]="refundingPayment()"
            (click)="refundPayment.emit()"
          >
            {{ t('orderDetails.refundPayment') }}
          </pxw-button>
        </div>
      </div>
    }

    <!-- Additional Info (e.g., for partial payments) -->
    @if (paymentData.additionalInfo) {
      <div class="order-section-item">
        <div class="order-section-item-title">{{ t('paymentStatus.additionalInfo') }}</div>
        <div class="order-section-item-value">
          {{ paymentData.additionalInfo }}
        </div>
      </div>
    }

    @if (paymentData.providerPaymentId) {
      <div class="order-section-item">
        <div class="order-section-item-title">{{ t('paymentStatus.transactionId') }}</div>
        <div class="order-section-item-value provider-id">
          {{ paymentData.providerPaymentId }}
        </div>
      </div>
    }

    <!-- Last Updated (for both types) -->
    @if (paymentData.updated) {
      <div class="order-section-item">
        <div class="order-section-item-title">
          {{ t('paymentStatus.lastUpdated') }}
        </div>
        <div class="order-section-item-value">
          {{ paymentData.updated | date: 'dd/MM/yy - HH:mm' }}hs
        </div>
      </div>
    }
  }

  @if (tipAmount() > 0) {
    <div class="order-section-item">
      <div class="order-section-item-title">{{ t('orderDetails.tip') }}</div>
      <div class="order-section-item-value">{{ tipAmount() | currencyFormat }}</div>
    </div>
  }
</ng-container>
